import {
  Text,
  Heading,
  Button,
  Input,
  Flex,
  Select,
} from '@qga/roo-ui/components';
import styled from '@emotion/styled';
import { FieldLabel, FieldError } from '../../Forms';
import { CALLBACK_TIMES, CALLBACK_DATES } from './constants';

import type { FC } from 'react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import type {
  CallbackFormFieldValues,
  PrefillableFields,
} from '../RequestCallbackForm';

const StyledSelect = styled(Select)`
  ::-ms-expand {
    display: none;
  }
`;

export const NAME_TEXT_REGEX = /^[a-z .'-\s]+$/i;
export const PHONE_REGEX = /^[0-9\s+-]+$/;
export const QFF_OR_EMPTY_REGEX = /(^$|^(([0-9]){10}|([0-9]){1,7})$)/;

export interface RequestCallbackDetailsProps {
  register: UseFormRegister<CallbackFormFieldValues>;
  errors: FieldErrors<CallbackFormFieldValues>;
  prefilledDetails?: PrefillableFields;
}

export const RequestCallbackDetails: FC<RequestCallbackDetailsProps> = ({
  register,
  errors,
  prefilledDetails: { fullName, phoneNumber, qffNumber } = {},
}) => (
  <>
    <Heading.h3 fontSize="xl" mb={6}>
      How we&apos;ll contact you
    </Heading.h3>
    <Heading.h4 fontSize="md">Your details</Heading.h4>
    <Flex
      flexDirection={['column', 'row']}
      flexWrap="wrap"
      borderBottom={1}
      borderColor="greys.alto"
      pb={5}
      mb={8}
    >
      <FieldLabel
        htmlFor="fullName"
        flex="0 0 50%"
        mb={[5, 3]}
        pr={[0, 4]}
        data-testid="full-name-input"
      >
        Name
        <Input
          id="fullName"
          defaultValue={fullName}
          {...register('fullName', {
            required: 'Please enter your name',
            pattern: {
              value: NAME_TEXT_REGEX,
              message:
                'Fullname cannot contain numerical or non-English characters',
            },
          })}
          error={!!errors.fullName}
        />
        <FieldError error={errors.fullName} data-testid="full-name-error" />
      </FieldLabel>
      <FieldLabel
        htmlFor="phoneNumber"
        flex="0 0 50%"
        mb={[5, 3]}
        pr={[0, 4]}
        data-testid="phone-number-input"
      >
        Phone number
        <Input
          id="phoneNumber"
          defaultValue={phoneNumber}
          {...register('phoneNumber', {
            required: 'Please enter your phone number',
            pattern: {
              value: PHONE_REGEX,
              message: 'Please enter only numbers, spaces, + or -',
            },
          })}
          error={!!errors.phoneNumber}
        />
        <FieldError
          error={errors.phoneNumber}
          data-testid="phone-number-error"
        />
      </FieldLabel>
      <FieldLabel
        htmlFor="qffNumber"
        flex="0 0 50%"
        mb={[5, 3]}
        pr={[0, 4]}
        data-testid="qff-number-input"
      >
        <Flex justifyContent="space-between" alignItems="center">
          Qantas Frequent Flyer number
          <Text color="greys.dusty" fontSize="xs">
            optional
          </Text>
        </Flex>
        <Input
          id="qffNumber"
          defaultValue={qffNumber}
          {...register('qffNumber', {
            pattern: {
              value: QFF_OR_EMPTY_REGEX,
              message: 'Please enter a valid Frequent Flyer Number',
            },
          })}
          error={!!errors.qffNumber}
        />
        <FieldError error={errors.qffNumber} data-testid="qff-number-error" />
        <Text>So we know how to award your Qantas Points</Text>
      </FieldLabel>
    </Flex>

    <Heading.h4 fontSize="md">When&apos;s the best time to call?</Heading.h4>
    <Flex flexDirection={['column', 'row']} flexWrap="wrap" mb={5}>
      <FieldLabel
        htmlFor="callbackTime"
        flex="0 0 50%"
        mb={[5, 3]}
        pr={[0, 4]}
        data-testid="callback-time-select"
      >
        <Flex justifyContent="space-between" alignItems="center">
          Time
          <Text color="greys.dusty" fontSize="xs">
            optional
          </Text>
        </Flex>
        <StyledSelect
          id="callbackTime"
          {...register('callbackTime')}
          error={!!errors.callbackTime}
        >
          <option value="">Select</option>
          {CALLBACK_TIMES.map((timeslot) => (
            <option key={timeslot.title} value={timeslot.value}>
              {timeslot.title}
            </option>
          ))}
        </StyledSelect>
        <FieldError
          error={errors.callbackTime}
          data-testid="callback-time-error"
        />
      </FieldLabel>
      <FieldLabel
        htmlFor="callbackDate"
        flex="0 0 50%"
        mb={[5, 3]}
        pr={[0, 4]}
        data-testid="callback-date-select"
      >
        <Flex justifyContent="space-between" alignItems="center">
          Date
          <Text color="greys.dusty" fontSize="xs">
            optional
          </Text>
        </Flex>
        <StyledSelect
          id="callbackDate"
          {...register('callbackDate')}
          error={!!errors.callbackDate}
        >
          <option value="">Select</option>
          {CALLBACK_DATES.map((timeslot) => (
            <option key={timeslot.title} value={timeslot.value}>
              {timeslot.title}
            </option>
          ))}
        </StyledSelect>
        <FieldError
          error={errors.callbackDate}
          data-testid="callback-date-error"
        />
      </FieldLabel>
    </Flex>

    <Button
      type="submit"
      variant="primary"
      width={['100%', '220px']}
      data-testid="submit-callback-button"
      mb={8}
    >
      Submit
    </Button>
    <Text display="block">
      We will only use your information for the purpose of processing this
      enquiry
    </Text>
  </>
);
